import { ValidationTrigger, createManagerFactory, formBuilder } from 'app-components/utils/createForm/formBuilder';
import { v4 as uuid } from 'uuid';
import {
  CategoryTargetConditionsType,
  ConditionsEnum,
  PayeeMatchingOperatorType,
  PayeeMatchingType,
  PayeeNameType,
  PayeeSourceConditionType,
  SubmitPayload,
  TRANSACTION_RULES_REDUCER_KEY,
  TransactionRuleConditionStatusType,
  TransactionRuleConditionStatusTypeKeys,
  TransactionRuleType,
  defaultPayeeSourceCondition,
} from './transactionRulesTypes';

import type RootState from 'app-components/utils/redux-store/rootState';
import { BUSINESS_REDUCER_KEY } from '../business';
import { getDefaultBusiness } from '../business/businessSelectors';
import { CATEGORIES_REDUCER_KEY, categoriesSelectors } from '../categories';
import { chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import { ENTITLEMENTS_REDUCER_KEY, entitlementsSelectors } from '../entitlements';
import { findEquivalentCategory, getUseDefaultAccountInitialValue } from './transactionRulesSelectors';

type ReduxState = Pick<
  RootState,
  | typeof TRANSACTION_RULES_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof BUSINESS_REDUCER_KEY
  | typeof ENTITLEMENTS_REDUCER_KEY
>;

export const clearState: TransactionRuleType = {
  id: undefined,
  payeeSourceCondition: defaultPayeeSourceCondition,
  amountSourceCondition: undefined,
  coaSourceConditions: undefined,
  accountSourceIds: undefined,
  usageSourceType: undefined,
  payeeTarget: undefined,
  coaTargetCondition: undefined,
  useDefaultAccountUsage: undefined,
  businessCoaTargetCondition: undefined,
  tagsTargetIds: undefined,
  memoTarget: undefined,
  isExcludedFromReportsTarget: undefined,
  isExcludedFromF2STarget: undefined,
  isReviewedTarget: undefined,
  applyToExistingTransactions: false,
  businessTargetId: undefined,
  // customerTargetId: undefined,
  // projectTargetId: undefined,
  // prevTransactionRuleId: undefined,
  // nextTransactionRuleId: undefined,
};

type FormState = SubmitPayload & {
  initialValues: Partial<TransactionRuleType>;
  isBusinessEnabled: boolean;
};

const init = ({ input, store }: { input: Partial<TransactionRuleType>; store: ReduxState }): FormState => {
  const useDefaultAccountUsage = getUseDefaultAccountInitialValue(store, input);
  const isBusinessEnabled = entitlementsSelectors.getBusinessEnabled(store);

  const transactionRule: TransactionRuleType = {
    id: input.id ?? undefined,
    payeeSourceCondition: input.payeeSourceCondition ?? undefined,
    amountSourceCondition: input.amountSourceCondition,
    coaSourceConditions: input.coaSourceConditions,
    accountSourceIds: input.accountSourceIds,
    usageSourceType: input.usageSourceType,
    payeeTarget: input.payeeTarget,
    coaTargetCondition: input.coaTargetCondition,
    useDefaultAccountUsage,
    businessCoaTargetCondition: input.businessCoaTargetCondition,
    tagsTargetIds: input.tagsTargetIds,
    memoTarget: input.memoTarget,
    isExcludedFromReportsTarget: input.isExcludedFromReportsTarget,
    isExcludedFromF2STarget: input.isExcludedFromF2STarget,
    isReviewedTarget: input.isReviewedTarget,
    applyToExistingTransactions: input.applyToExistingTransactions,
    businessTargetId: input.businessTargetId,
  };
  const enabled: TransactionRuleConditionStatusType = {
    payeeSourceCondition: true, // hardcoded for Phase 1
    // payeeSourceCondition: !!input.payeeSourceCondition,
    amountSourceCondition: !!input.amountSourceCondition,
    coaSourceConditions: !!input.coaSourceConditions,
    accountSourceIds: !!input.accountSourceIds,
    usageSourceType: !!input.usageSourceType,
    payeeTarget: !!input.payeeTarget,
    coaTargetCondition: input.coaTargetCondition !== undefined,
    tagsTargetIds: !!input.tagsTargetIds,
    memoTarget: !!input.memoTarget,
    isExcludedFromReportsTarget: !!input.isExcludedFromReportsTarget,
    isExcludedFromF2STarget: !!input.isExcludedFromF2STarget,
    isReviewedTarget: !!input.isReviewedTarget,
  };

  return {
    transactionRule,
    enabled,
    initialValues: input,
    isBusinessEnabled,
  };
};

export const selectInitialValues = ({ state }: { state: FormState }) => state.initialValues;
export const selectInitialPayeeSourceCondition = (args: { state: FormState }) =>
  selectInitialValues(args).payeeSourceCondition;

const transactionRulesForm = formBuilder({ init })
  // Enabled flags - start
  .addField('isEnabledPayeeSource', {
    selector: (state) => state.enabled.payeeSourceCondition,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.payeeSourceCondition = value;
    },
  })
  // .addField('isEnabledAmountSource', {
  //   selector: (state) => state.enabled.amountSourceCondition,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.amountSourceCondition = value;
  //   },
  // })
  // .addField('isEnabledCoaSource', {
  //   selector: (state) => state.enabled.coaSourceConditions,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.coaSourceConditions = value;
  //   },
  // })
  // .addField('isEnabledAccountSourceIds', {
  //   selector: (state) => state.enabled.accountSourceIds,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.accountSourceIds = value;
  //   },
  // })
  // .addField('isEnabledUsageSourceType', {
  //   selector: (state) => state.enabled.usageSourceType,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.usageSourceType = value;
  //   },
  // })
  .addField('isEnabledPayeeTarget', {
    selector: (state) => state.enabled.payeeTarget,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.payeeTarget = value;
    },
  })
  .addField('isEnabledCoaTarget', {
    selector: (state) => state.enabled.coaTargetCondition,
    onValueChange: (draft, value: boolean) => {
      draft.enabled.coaTargetCondition = value;
    },
  })
  // .addField('isEnabledTagsTargetIds', {
  //   selector: (state) => state.enabled.tagsTargetIds,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.tagsTargetIds = value;
  //   },
  // })
  // .addField('isEnabledMemoTarget', {
  //   selector: (state) => state.enabled.memoTarget,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.memoTarget = value;
  //   },
  // })
  // .addField('isEnabledExcludedFromReportsTarget', {
  //   selector: (state) => state.enabled.isExcludedFromReportsTarget,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.isExcludedFromReportsTarget = value;
  //   },
  // })
  // .addField('isEnabledExcludedFromF2STarget', {
  //   selector: (state) => state.enabled.isExcludedFromF2STarget,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.isExcludedFromF2STarget = value;
  //   },
  // })
  // .addField('isEnabledReviewedTarget', {
  //   selector: (state) => state.enabled.isReviewedTarget,
  //   onValueChange: (draft, value: boolean) => {
  //     draft.enabled.isReviewedTarget = value;
  //   },
  // })
  // Enabled flags - end

  // Payee Source - start
  .addField('payeeSource', {
    selector: (state) => state.transactionRule.payeeSourceCondition || defaultPayeeSourceCondition,
    onValueChange: (draft, value: PayeeSourceConditionType) => {
      if (draft.transactionRule.payeeSourceCondition) {
        draft.transactionRule.payeeSourceCondition = value;
      }
    },
  })
  .addField('payeeSourceType', {
    selector: (state) => state.transactionRule.payeeSourceCondition?.payeeType || 'STATEMENT',
    onValueChange: (draft, value: PayeeNameType) => {
      if (draft.transactionRule.payeeSourceCondition?.payeeType) {
        draft.transactionRule.payeeSourceCondition.payeeType = value;
      }
    },
  })
  .addMultiField('payeeSourceMatchingOperator', {
    selectActiveKeys(state) {
      const indexes: number[] = [];
      if (state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria) {
        for (let i = 0; i < state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria.length; i++) {
          indexes.push(i);
        }
      }
      return indexes;
    },
    onValueChange: (draft, key, value: PayeeMatchingOperatorType) => {
      if (draft.transactionRule.payeeSourceCondition) {
        draft.transactionRule.payeeSourceCondition.payeeMatchingCriteria[key].payeeMatchingOperator = value;
      }
    },
    selector: (state, key) =>
      state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria[key].payeeMatchingOperator || 'CONTAINS',
  })
  .addMultiField('payeeSourceName', {
    selectActiveKeys(state) {
      const indexes: number[] = [];
      if (state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria) {
        for (let i = 0; i < state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria.length; i++) {
          indexes.push(i);
        }
      }
      return indexes;
    },
    onValueChange: (draft, key, value: string) => {
      if (value.trim().length === 0 && key != 0) {
        // if any condition after the first one is blank splice it out of the array, otherwise we have blank conditions
        draft.transactionRule.payeeSourceCondition?.payeeMatchingCriteria.splice(key, 1);
      } else if (draft.transactionRule.payeeSourceCondition) {
        draft.transactionRule.payeeSourceCondition.payeeMatchingCriteria[key].payeeName = value;
      }
    },
    selector: (state, key) => state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria[key].payeeName || '',
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          return !!state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria[key].payeeName;
        },
        error: 'Required',
      },
      length: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state, key) => {
          return (state.transactionRule.payeeSourceCondition?.payeeMatchingCriteria[key].payeeName?.length ?? 0) >= 3;
        },
        error: 'Please enter payee pattern that is at least 3 characters',
      },
    },
  })

  // Payee Actions
  .addAction('addPayeeSourceMatchingCriteria', (draft, value: PayeeMatchingType) => {
    draft.transactionRule.payeeSourceCondition?.payeeMatchingCriteria.push(value);
  })
  .addAction('removePayeeSourceMatchingCriteria', (draft, index: number) => {
    draft.transactionRule.payeeSourceCondition?.payeeMatchingCriteria.splice(index, 1);
  })
  // Payee Source - end

  .addAction('showCondition', (draft, payload: { condition: ConditionsEnum; value: boolean }, store: ReduxState) => {
    draft.enabled[payload.condition] = payload.value;
    if (payload.condition === ConditionsEnum.COA_TARGET_CONDITION) {
      if (draft.transactionRule.coaTargetCondition) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.coaTargetCondition.id);
        if (equivalent?.id) {
          draft.transactionRule.businessCoaTargetCondition = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      } else if (draft.transactionRule.businessCoaTargetCondition) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.businessCoaTargetCondition.id);
        if (equivalent?.id) {
          draft.transactionRule.coaTargetCondition = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      }
    }
  })

  // Payee Target
  .addField('payeeTarget', {
    selector: (state) => state.transactionRule.payeeTarget || '',
    onValueChange: (draft, value: string) => {
      draft.transactionRule.payeeTarget = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.payeeTarget ||
            (!!state.transactionRule.payeeTarget && state.transactionRule.payeeTarget.trim() !== '')
          );
        },
        error: 'Required',
      },
    },
  })
  // Payee Target - end

  // COA Target - start
  .addField('coaTarget', {
    selector: (state) => state.transactionRule.coaTargetCondition,
    onValueChange: (draft, value: CategoryTargetConditionsType | undefined, store) => {
      draft.transactionRule.coaTargetCondition = value;
      // set exclude values based on category - the user can override these values explictly when we open that capability in the rule.
      const excludeValue = !!chartOfAccountsUtils.isTransferCOA(value);
      draft.transactionRule.isExcludedFromReportsTarget = excludeValue;
      draft.transactionRule.isExcludedFromF2STarget = excludeValue;

      const isBizCat =
        value?.type === 'CATEGORY'
          ? categoriesSelectors.getCategoryById(store, value.id)?.usageType === 'BUSINESS'
          : undefined;

      // Set biz id if it is a biz category, user can explicitly override.
      if (!draft.transactionRule.businessTargetId && isBizCat) {
        const bizId = getDefaultBusiness(store)?.id;
        draft.transactionRule.businessTargetId = bizId;
      } else if (!isBizCat) {
        draft.transactionRule.businessTargetId = undefined;
      }

      if (
        draft.isBusinessEnabled &&
        draft.transactionRule.useDefaultAccountUsage &&
        !draft.transactionRule.businessCoaTargetCondition &&
        draft.transactionRule.coaTargetCondition
      ) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.coaTargetCondition.id);
        if (equivalent?.id) {
          draft.transactionRule.businessCoaTargetCondition = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return !state.enabled.coaTargetCondition || !!state.transactionRule.coaTargetCondition;
        },
        error: 'Required',
      },
    },
  })

  .addField('businessCoaTarget', {
    selector: (state) => state.transactionRule.businessCoaTargetCondition,
    onValueChange: (draft, value: CategoryTargetConditionsType | undefined, store) => {
      draft.transactionRule.businessCoaTargetCondition = value;
      // set exclude values based on category - the user can override these values explictly when we open that capability in the rule.
      const excludeValue = !!chartOfAccountsUtils.isTransferCOA(value);
      draft.transactionRule.isExcludedFromReportsTarget = excludeValue;
      draft.transactionRule.isExcludedFromF2STarget = excludeValue;

      const isBizCat =
        value?.type === 'CATEGORY'
          ? categoriesSelectors.getCategoryById(store, value.id)?.usageType === 'BUSINESS'
          : undefined;

      // Set biz id if it is a biz category, user can explicitly override.
      if (!draft.transactionRule.businessTargetId && isBizCat) {
        const bizId = getDefaultBusiness(store)?.id;
        draft.transactionRule.businessTargetId = bizId;
      } else if (!isBizCat) {
        draft.transactionRule.businessTargetId = undefined;
      }

      if (
        draft.transactionRule.useDefaultAccountUsage &&
        !draft.transactionRule.coaTargetCondition &&
        draft.transactionRule.businessCoaTargetCondition
      ) {
        const equivalent = findEquivalentCategory(store, draft.transactionRule.businessCoaTargetCondition.id);
        if (equivalent?.id) {
          draft.transactionRule.coaTargetCondition = {
            id: equivalent.id,
            type: equivalent.id === '0' ? 'UNCATEGORIZED' : 'CATEGORY',
          };
        }
      }
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => {
          return (
            !state.enabled.coaTargetCondition ||
            !state.transactionRule.useDefaultAccountUsage ||
            !!state.transactionRule.businessCoaTargetCondition
          );
        },
        error: 'Required',
      },
    },
  })

  .addField('useDefaultAccountUsage', {
    selector: (state) => state.transactionRule.useDefaultAccountUsage || false,
    onValueChange: (draft, value: boolean) => {
      draft.transactionRule.useDefaultAccountUsage = value;
      draft.transactionRule.coaTargetCondition = undefined;
      draft.transactionRule.businessCoaTargetCondition = undefined;
      draft.transactionRule.businessTargetId = undefined;
    },
  })
  // COA Target - end

  // bizId Target - start
  .addField('businessTargetId', {
    selector: (state) => state.transactionRule.businessTargetId,
    onValueChange: (draft, value: string | undefined) => {
      // If we switch from biz -> personal or personal -> biz. Set cat to undefined
      if ((!value && draft.transactionRule.businessTargetId) || (!draft.transactionRule.businessTargetId && value)) {
        draft.transactionRule.coaTargetCondition = { type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED, id: '0' };
      }
      draft.transactionRule.businessTargetId = value;
    },
  })
  // bizId Target - end

  // Apply to Existing - start
  .addField('applyToExistingTransactions', {
    selector: (state) => state.transactionRule.applyToExistingTransactions || false,
    onValueChange: (draft, value: boolean) => {
      draft.transactionRule.applyToExistingTransactions = value;
    },
  })
  // Apply to Existing - end

  .build({
    submitTransform(state): SubmitPayload {
      // at this point, remove all enabled === false properties from the transactionRule
      const pruned = { ...state.transactionRule };
      const keys = Object.keys(state.enabled) as TransactionRuleConditionStatusTypeKeys[];
      const filteredProps = keys.filter(
        (key: keyof TransactionRuleConditionStatusType) => state.enabled[key] === false,
      );
      filteredProps.forEach((key: keyof TransactionRuleConditionStatusType) => {
        // delete props that are disabled OR the value is undefined.
        if (
          (state.enabled[key] === false && !!state.transactionRule[key]) ||
          state.transactionRule[key] === undefined
        ) {
          delete pruned[key];
        }
      });

      const value = {
        ...state,
        transactionRule: {
          ...pruned,
          ...(state.transactionRule.id ? { id: state.transactionRule.id } : { clientId: uuid().toUpperCase() }),
        },
      };

      return value;
    },
  });

export const transactionRulesFormFactory = createManagerFactory(transactionRulesForm);
